import { useState, useEffect } from 'react';
import { FiChevronDown, FiChevronUp, FiClock } from 'react-icons/fi';
import { contractApi } from '../../../services/contractApi';
import type { ContractTypeAuditEntry } from '../../../services/contractApi';

interface AuditHistoryPanelProps {
    contractTypeName: string;
}

const AuditHistoryPanel = ({ contractTypeName }: AuditHistoryPanelProps) => {
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [entries, setEntries] = useState<ContractTypeAuditEntry[]>([]);

    useEffect(() => {
        if (expanded && entries.length === 0) {
            loadAudit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded]);

    const loadAudit = async () => {
        setLoading(true);
        try {
            const data = await contractApi.getContractTypeAudit(contractTypeName);
            setEntries(data);
        } catch {
            // silent fail for audit panel
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr: string) => {
        const d = new Date(dateStr);
        return d.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    /** Describes what a list-based change did, e.g. "adicionou CORE, removeu CLEAN". */
    const diffLists = (
        previous: string[],
        next: string[],
        singular: string,
    ): string => {
        const added = next.filter(i => !previous.includes(i));
        const removed = previous.filter(i => !next.includes(i));
        const parts: string[] = [];
        if (added.length > 0) parts.push(`adicionou ${singular} ${added.join(', ')}`);
        if (removed.length > 0) parts.push(`removeu ${singular} ${removed.join(', ')}`);
        if (parts.length === 0) return 'Salvou sem alterações';
        // Capitalize first letter
        const text = parts.join('; ');
        return text.charAt(0).toUpperCase() + text.slice(1);
    };

    const asStringList = (raw: unknown): string[] => {
        if (!Array.isArray(raw)) return [];
        return raw.filter((i): i is string => typeof i === 'string');
    };

    const asObserverList = (raw: unknown): string[] => {
        if (!Array.isArray(raw)) return [];
        return raw
            .filter((o: any) => o && typeof o === 'object' && o.email)
            .map((o: any) => `${o.name || o.email} (${o.email})`);
    };

    const summarizeStructures = (entry: ContractTypeAuditEntry): string => {
        const prev = entry.previousStructures as any;
        const next = entry.newStructures as any;
        if (!prev) return 'Configuração inicial dos papéis';

        const prevRolesArr: any[] = prev?.[0]?.roles || [];
        const newRolesArr: any[] = next?.[0]?.roles || [];

        const prevNames = prevRolesArr.map((r: any) => r.role);
        const newNames = newRolesArr.map((r: any) => r.role);
        const added = newNames.filter((r: string) => !prevNames.includes(r));
        const removed = prevNames.filter((r: string) => !newNames.includes(r));

        const parts: string[] = [];
        if (added.length > 0) parts.push(`adicionou o papel ${added.join(', ')}`);
        if (removed.length > 0) parts.push(`removeu o papel ${removed.join(', ')}`);

        // Detect field-level changes on roles kept in both versions
        const changed: string[] = [];
        newRolesArr.forEach((nr: any) => {
            const pr = prevRolesArr.find((r: any) => r.role === nr.role);
            if (!pr) return;
            const nMin = nr.minCount ?? nr.min_count ?? 1;
            const pMin = pr.minCount ?? pr.min_count ?? 1;
            const nOrder = nr.signingOrder ?? nr.signing_order ?? 1;
            const pOrder = pr.signingOrder ?? pr.signing_order ?? 1;
            const nSource = nr.participantSource ?? nr.participant_source ?? 'ANY';
            const pSource = pr.participantSource ?? pr.participant_source ?? 'ANY';
            const details: string[] = [];
            if (nMin !== pMin) details.push(`mínimo ${pMin}→${nMin}`);
            if (nOrder !== pOrder) details.push(`ordem ${pOrder}→${nOrder}`);
            if (nSource !== pSource) details.push(`fonte ${pSource}→${nSource}`);
            if (details.length > 0) changed.push(`${nr.role} (${details.join(', ')})`);
        });
        if (changed.length > 0) parts.push(`alterou ${changed.join('; ')}`);

        if (parts.length === 0) return 'Salvou sem alterações';
        const text = parts.join('; ');
        return text.charAt(0).toUpperCase() + text.slice(1);
    };

    const summarizeChanges = (entry: ContractTypeAuditEntry): string => {
        switch (entry.changeType) {
            case 'ALLOWED_PRODUCTS':
                return diffLists(
                    asStringList(entry.previousStructures),
                    asStringList(entry.newStructures),
                    'o produto',
                );
            case 'DEFAULT_OBSERVERS':
                return diffLists(
                    asObserverList(entry.previousStructures),
                    asObserverList(entry.newStructures),
                    'o observador',
                );
            case 'STRUCTURES':
            default:
                return summarizeStructures(entry);
        }
    };

    const CHANGE_TYPE_LABELS: Record<string, { label: string; classes: string }> = {
        STRUCTURES: { label: 'Papéis', classes: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
        DEFAULT_OBSERVERS: { label: 'Observadores', classes: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
        ALLOWED_PRODUCTS: { label: 'Produtos', classes: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
    };

    return (
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
            {/* Collapsible header */}
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors rounded-lg"
            >
                <div className="flex items-center gap-2">
                    <FiClock className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">Histórico de Alterações</h3>
                </div>
                {expanded
                    ? <FiChevronUp className="w-5 h-5 text-gray-500" />
                    : <FiChevronDown className="w-5 h-5 text-gray-500" />
                }
            </button>

            {/* Content */}
            {expanded && (
                <div className="px-6 pb-4 border-t border-gray-200 dark:border-slate-700">
                    {loading && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">Carregando...</p>
                    )}

                    {!loading && entries.length === 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">Nenhuma alteração registrada</p>
                    )}

                    {!loading && entries.length > 0 && (
                        <div className="space-y-3 mt-4">
                            {entries.map(entry => (
                                <div key={entry.id} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
                                    <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-blue-500" />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-medium text-gray-900 dark:text-white">{entry.userId}</span>
                                            {(() => {
                                                const meta = CHANGE_TYPE_LABELS[entry.changeType || 'STRUCTURES'];
                                                return meta
                                                    ? <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${meta.classes}`}>{meta.label}</span>
                                                    : null;
                                            })()}
                                            <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(entry.changedAt)}</span>
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">{summarizeChanges(entry)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AuditHistoryPanel;
